import React, { useState } from "react";
import { Loader2, MessageCircle, Truck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import client from "@/lib/api";
import { payForBook, UPGRADE_PRICE } from "@/lib/pay";
import UpiPay from "@/components/UpiPay";
import { shipping as validateShipping, maxLen, required, firstError } from "@/lib/validate";

const MAX_REVISIONS = 2;

// What a family can do with a book that has already arrived: ask for changes,
// or move a digital copy up to a printed one.
export default function BookActions({ book, user, onChanged }) {
    const [askOpen, setAskOpen] = useState(false);
    const [upOpen, setUpOpen] = useState(false);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [method, setMethod] = useState("razorpay");
    const [upiOrder, setUpiOrder] = useState(null);
    const [addr, setAddr] = useState({ name: "", line1: "", city: "", pin: "", phone: "" });

    const left = MAX_REVISIONS - (book.revisions_used || 0);
    const delivered = book.status === "delivered";
    const canUpgrade = book.purchased && book.purchase_kind === "pdf";
    const addrError = validateShipping(addr);
    const addressOk = !addrError;

    const askForChanges = async () => {
        const bad = firstError([required(note, "A note"), maxLen(note, 2000, "Note")]);
        if (bad) { toast.error(bad); return; }
        setBusy(true);
        try {
            const { data } = await client.post(`/books/${book.id}/feedback`, { feedback: note });
            toast.success(data.revisions_left > 0
                ? `On it. You have ${data.revisions_left} revision left after this.`
                : "On it. That was your last revision — anything more, just reply to your order email.");
            setAskOpen(false); setNote("");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send that");
        } finally { setBusy(false); }
    };

    const upgrade = async () => {
        setBusy(true);
        try {
            const order = await payForBook({
                bookId: book.id, kind: "hardcover", method, shipping: addr,
                title: book.title, contact: { name: user?.name, email: user?.email, phone: user?.phone },
            });
            if (order.upi) { setUpOpen(false); setUpiOrder(order); return; }
            toast.success("Order placed — we'll print and post it.");
            setUpOpen(false);
            onChanged?.();
        } catch (e) {
            if (e?.cancelled) return;
            toast.error(e?.response?.data?.detail || e?.message || "Payment failed");
        } finally { setBusy(false); }
    };

    if (!delivered && !canUpgrade) return null;

    return (
        <>
            <div className="mt-2 flex flex-col gap-2">
                {delivered && left > 0 && (
                    <button onClick={(e) => { e.preventDefault(); setAskOpen(true); }}
                        data-testid={`ask-changes-${book.id}`}
                        className="w-full rounded-full min-h-9 py-2 px-2 text-[11px] leading-tight font-medium text-center
                                   border border-bubblegum/40 text-bubblegum bg-white hover:bg-bubblegum/5
                                   flex items-center justify-center gap-1.5 transition-colors">
                        <MessageCircle className="w-3.5 h-3.5 shrink-0" />
                        Ask for changes · {left} left
                    </button>
                )}
                {delivered && left === 0 && (
                    <span className="text-xs text-plum/35 text-center">Both revisions used</span>
                )}
                {canUpgrade && (
                    <button onClick={(e) => { e.preventDefault(); setUpOpen(true); }}
                        data-testid={`upgrade-${book.id}`}
                        className="w-full rounded-full min-h-9 py-2 px-2 text-[11px] leading-tight font-medium text-center
                                   border border-plum/15 text-plum bg-white hover:bg-lavender/30
                                   flex items-center justify-center gap-1.5 transition-colors">
                        <Truck className="w-3.5 h-3.5 shrink-0" />
                        Get it printed · ₹{UPGRADE_PRICE}
                    </button>
                )}
            </div>

            <Dialog open={askOpen} onOpenChange={setAskOpen}>
                <DialogContent className="max-w-lg" data-testid="revision-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-plum flex items-center gap-2">
                            <MessageCircle className="w-5 h-5 text-bubblegum" /> What should we change?
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-plum/60">
                        The same illustrator picks your book back up. It's back in your library within
                        48 hours, and your current copy stays downloadable while you wait.
                    </p>
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={5} maxLength={2000}
                        data-testid="revision-note"
                        placeholder="e.g. Could the dog be brown like ours, and can page 4 say “Goodnight, Habsi” instead?"
                        className="w-full mt-2 rounded-2xl border border-input bg-white p-4 text-sm text-plum
                                   focus:outline-none focus:ring-2 focus:ring-bubblegum/40 resize-none" />
                    <div className="text-xs text-plum/40">
                        Revision {(book.revisions_used || 0) + 1} of {MAX_REVISIONS}. Be as specific as you like.
                    </div>
                    <Button onClick={askForChanges} disabled={busy || note.trim().length < 5}
                        data-testid="revision-submit"
                        className="w-full rounded-full bg-bubblegum hover:bg-bubblegum/90 mt-1">
                        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                        {busy ? "Sending…" : "Send to our illustrator"}
                    </Button>
                </DialogContent>
            </Dialog>

            <Dialog open={upOpen} onOpenChange={setUpOpen}>
                <DialogContent className="max-w-lg" data-testid="upgrade-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-plum flex items-center gap-2">
                            <Truck className="w-5 h-5 text-bubblegum" /> Get it printed
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-plum/60">
                        A real hardback of “{book.title || "your storybook"}”, posted to your door.
                        You've already paid for the artwork, so this is just ₹{UPGRADE_PRICE} for
                        printing and postage. Your PDF stays yours either way.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3 mt-2">
                        <Input placeholder="Full name" value={addr.name} maxLength={60} className="bg-white"
                            onChange={(e) => setAddr({ ...addr, name: e.target.value })} data-testid="up-name" />
                        <Input placeholder="Contact phone" value={addr.phone} maxLength={20} className="bg-white"
                            onChange={(e) => setAddr({ ...addr, phone: e.target.value })} data-testid="up-phone" />
                        <Input placeholder="Address line" value={addr.line1} maxLength={200} className="sm:col-span-2 bg-white"
                            onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="up-line1" />
                        <Input placeholder="City" value={addr.city} maxLength={120} className="bg-white"
                            onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="up-city" />
                        <Input placeholder="PIN code" value={addr.pin} inputMode="numeric" maxLength={6}
                            className="bg-white" data-testid="up-pin"
                            onChange={(e) => setAddr({ ...addr, pin: e.target.value.replace(/\D/g, "") })} />
                    </div>
                    {addrError && Object.values(addr).some(Boolean) && (
                        <div className="text-xs text-bubblegum" data-testid="upgrade-addr-error">{addrError}</div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-plum/50">Pay with</span>
                        <div className="flex gap-1 p-1 rounded-full bg-lavender/40">
                            {[["razorpay", "Card / netbanking"], ["upi", "UPI"]].map(([m, label]) => (
                                <button key={m} onClick={() => setMethod(m)} data-testid={`up-method-${m}`}
                                    className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                                        method === m ? "bg-white text-plum shadow-sm" : "text-plum/60"}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Button onClick={upgrade} disabled={busy || !addressOk} data-testid="upgrade-pay-btn"
                        className="w-full rounded-full bg-bubblegum hover:bg-bubblegum/90 mt-2">
                        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                        {busy ? "Processing…" : `Pay ₹${UPGRADE_PRICE}`}
                    </Button>
                </DialogContent>
            </Dialog>

            <UpiPay order={upiOrder} open={!!upiOrder}
                onOpenChange={(o) => { if (!o) setUpiOrder(null); }}
                onClaimed={() => {
                    setUpiOrder(null);
                    toast.success("Thanks — we'll confirm your payment and start printing.");
                    onChanged?.();
                }} />
        </>
    );
}
